import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAllApplicationsAdmin } from '../../services/doctorService';
import { submitENach } from '../../services/financialservice';
import { RefreshCw, CheckCircle2, Loader2, Building } from 'lucide-react';

// PolicyENachHospitalView
// Shows applications with e-NACH initiated/completed and allows uploading proof
// to mark e-NACH as done (same flow as hospital page) from the Policy portal.
const PolicyENachHospitalView = () => {
  const { userData: user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  // Compact view: no upload actions needed here
  const [busy, setBusy] = useState({}); // { [appId]: boolean }
  const [message, setMessage] = useState({}); // { [appId]: string }
  const [error, setError] = useState({}); // { [appId]: string }
  const [initiateForm, setInitiateForm] = useState({}); // { [appId]: { link, comments } }
  const [bucket, setBucket] = useState('to_initiate'); // not_started | to_initiate | initiated | completed | all

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllApplicationsAdmin({ _fetchAll: true }, 1, 50);
      // Keep a broader list; we'll bucket in-memory
      const list = (res?.applications || []);
      setApps(list);
    } catch (e) {
      console.error('Error loading e-NACH list (policy):', e);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApps(); }, [loadApps]);

  const initiatedApps = useMemo(() => apps.filter(a => (a?.workflow?.enach?.status) === 'initiated'), [apps]);
  const completedApps = useMemo(() => apps.filter(a => (a?.workflow?.enach?.status) === 'completed'), [apps]);
  const notStartedApps = useMemo(() => {
    return apps.filter(a => {
      const w = a?.workflow || {};
      const en = w.enach || w.eNach || {};
      const status = String(en?.status || '').toLowerCase();
      return !status || ['pending', 'setup_pending', 'not_started', 'new', 'none'].includes(status);
    });
  }, [apps]);
  const toInitiateApps = useMemo(() => {
    return apps.filter(a => {
      const w = a?.workflow || {};
      const en = w.enach || w.eNach || {};
      const ds = w.dischargeSettlement || {};
      const falAccepted = ds?.hospitalAcknowledgement?.status === 'accepted';
      // Show as candidates if FAL accepted (or existing pipeline) and eNACH not yet initiated/completed
      return (!en?.status || ['pending', 'setup_pending'].includes(String(en.status).toLowerCase())) && falAccepted;
    });
  }, [apps]);

  const handleInitiate = async (app) => {
    const appId = app.applicationId;
    const form = initiateForm[appId] || {};
    setBusy((p) => ({ ...p, [appId]: true }));
    setError((p) => ({ ...p, [appId]: '' }));
    try {
      const res = await submitENach(appId, user?.uid || 'policy-user', {
        link: (form.link || '').trim(),
        comments: (form.comments || '').trim()
      });
      if (res?.success) {
        setMessage((p) => ({ ...p, [appId]: 'e-NACH initiated' }));
        await loadApps();
      } else {
        setError((p) => ({ ...p, [appId]: res?.error || 'Failed to initiate' }));
      }
    } catch (e) {
      setError((p) => ({ ...p, [appId]: e?.message || 'Failed to initiate' }));
    } finally {
      setBusy((p) => ({ ...p, [appId]: false }));
    }
  };

  // handleMarkDone removed in compact view

  const onRefresh = async () => {
    setRefreshing(true);
    await loadApps();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
        <span className="text-gray-600">Loading e-NACH…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">e-NACH (Hospital Proof)</h1>
          <p className="text-gray-600">Upload and track e-NACH completion proofs from hospitals</p>
        </div>
        <button onClick={onRefresh} disabled={refreshing} className="btn-outline disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}/>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Buckets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-2 border-b border-gray-100 flex items-center space-x-4 overflow-x-auto">
          {[
            { id: 'not_started', label: 'Not Started', count: notStartedApps.length },
            { id: 'to_initiate', label: 'To Initiate', count: toInitiateApps.length },
            { id: 'initiated', label: 'Initiated', count: initiatedApps.length },
            { id: 'completed', label: 'Completed', count: completedApps.length },
            { id: 'all', label: 'All', count: apps.length }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setBucket(t.id)}
              className={`flex items-center space-x-2 py-2 px-3 rounded border text-sm whitespace-nowrap ${bucket === t.id ? 'border-teal-500 text-teal-700 bg-teal-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              <span>{t.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${bucket === t.id ? 'bg-white text-teal-700 border border-teal-300' : 'bg-gray-100 text-gray-700'}`}>{t.count}</span>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 text-xs text-gray-500">Tip: Click anywhere on a case to open Policy Review (eNACH tab).</div>
      </div>

      {/* Not Started */}
      {(bucket === 'not_started' || bucket === 'all') && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Not Started ({notStartedApps.length})</h3>
        </div>
        {notStartedApps.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No cases with e-NACH not started.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notStartedApps.map((app) => {
              const appId = app.applicationId;
              return (
                <div
                  key={app.id}
                  className="p-4 group cursor-pointer hover:bg-gray-50 transition-colors rounded-md"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/policy/details/${appId}?tab=enach`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/policy/details/${appId}?tab=enach`); } }}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); navigate(`/policy/details/${appId}?tab=enach`); }}
                          className="text-indigo-600 hover:text-indigo-700 hover:underline"
                          title="Open case in new tab"
                        >
                          {app.patient?.name || 'Patient'}
                        </button>
                        <span className="text-xs text-gray-500"> #{appId}</span>
                      </div>
                      <div className="text-xs text-gray-600 flex items-center"><Building className="h-3 w-3 mr-1 text-gray-400"/>{app.hospitalName || '—'}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200">Not Started</span>
                  </div>

                  {/* Compact view: only show the name (no inputs/buttons) */}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* To Initiate */}
      {(bucket === 'to_initiate' || bucket === 'all') && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">To Initiate ({toInitiateApps.length})</h3>
        </div>
        {toInitiateApps.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No cases awaiting e-NACH initiation.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {toInitiateApps.map((app) => {
              const appId = app.applicationId;
              const form = initiateForm[appId] || { link: '', comments: '' };
              return (
                <div
                  key={app.id}
                  className="p-4 group cursor-pointer hover:bg-gray-50 transition-colors rounded-md"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/policy/details/${appId}?tab=enach`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/policy/details/${appId}?tab=enach`); } }}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); navigate(`/policy/details/${appId}?tab=enach`); }}
                          className="text-indigo-600 hover:text-indigo-700 hover:underline"
                          title="Open case in new tab"
                        >
                          {app.patient?.name || 'Patient'}
                        </button>
                        <span className="text-xs text-gray-500"> #{appId}</span>
                      </div>
                      <div className="text-xs text-gray-600 flex items-center"><Building className="h-3 w-3 mr-1 text-gray-400"/>{app.hospitalName || '—'}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Awaiting Initiation</span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">e-NACH Link (optional)</label>
                      <input
                        type="url"
                        value={form.link || ''}
                        onChange={(e) => setInitiateForm((p) => ({ ...p, [appId]: { ...(p[appId] || {}), link: e.target.value } }))}
                        placeholder="https://mandate-url.example/…"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Comments (optional)</label>
                      <input
                        type="text"
                        value={form.comments || ''}
                        onChange={(e) => setInitiateForm((p) => ({ ...p, [appId]: { ...(p[appId] || {}), comments: e.target.value } }))}
                        placeholder="Any note for hospital"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                      />
                    </div>
                  </div>
                  {error[appId] && <p className="text-xs text-red-600 mt-2">{error[appId]}</p>}
                  {message[appId] && <p className="text-xs text-green-700 mt-2">{message[appId]}</p>}
                  <div className="mt-2">
                    <button
                      onClick={() => handleInitiate(app)}
                      disabled={!!busy[appId]}
                      className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 text-sm font-medium"
                    >
                      {busy[appId] ? <span className="inline-flex items-center"><Loader2 className="h-4 w-4 animate-spin mr-2"/>Initiating…</span> : 'Create / Initiate e-NACH'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Initiated */}
      {(bucket === 'initiated' || bucket === 'all') && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Pending Proof Upload ({initiatedApps.length})</h3>
        </div>
        {initiatedApps.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No pending e-NACH proofs.</div>
        ) : (
          <div className="divide-y divide-gray-100">
                {initiatedApps.map((app) => {
              // const en = app?.workflow?.enach || {};
              const appId = app.applicationId;
              return (
                <div key={app.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900 flex items-center">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); navigate(`/policy/details/${appId}?tab=policy-review`); }}
                          className="mr-2 text-indigo-600 hover:text-indigo-700 hover:underline"
                          title="Open case in new tab"
                        >
                          {app.patient?.name || 'Patient'}
                        </button>
                        <span className="text-xs text-gray-500">#{appId}</span>
                      </div>
                      <div className="text-xs text-gray-600 flex items-center"><Building className="h-3 w-3 mr-1 text-gray-400"/>{app.hospitalName || '—'}</div>
                      {/* Compact: hide link and comments */}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Initiated</span>
                    </div>
                  </div>
                  {/* Compact view: only show the name (no details or actions) */}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Completed */}
      {(bucket === 'completed' || bucket === 'all') && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Completed ({completedApps.length})</h3>
        </div>
        {completedApps.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No completed e-NACH yet.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {completedApps.map((app) => {
              const appId = app.applicationId;
              const en = app?.workflow?.enach || {};
              return (
                <div
                  key={app.id}
                  className="p-4 flex items-center justify-between group cursor-pointer hover:bg-gray-50 transition-colors rounded-md"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/policy/details/${appId}?tab=enach`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/policy/details/${appId}?tab=enach`); } }}
                >
                  <div>
                    <div className="font-medium text-gray-900">
                        <button
                        type="button"
                      onClick={() => { navigate(`/policy/details/${appId}?tab=enach`); }}
                        className="text-indigo-600 hover:text-indigo-700 hover:underline"
                        title="Open case in new tab"
                      >
                        {app.patient?.name || 'Patient'}
                      </button>
                      <span className="text-xs text-gray-500"> #{appId}</span>
                    </div>
                    {en?.link && (
                      <div className="text-xs mt-1"><a href={en.link} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{en.link}</a></div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { navigate(`/policy/details/${appId}?tab=enach`); }}
                      className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                      title="Open case in new tab"
                    >
                      Open Case
                    </button>
                    <span className="inline-flex items-center text-green-700 text-xs bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle2 className="h-3 w-3 mr-1"/>Completed</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PolicyENachHospitalView;


